import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subscription, ReplaySubject, BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

// Import the services we've already created
import { GoogleAuthService } from './google-auth.service';
import { UtilsService } from './utils.service';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { FirebaseService } from './firebase.service';
import { IntercomService } from './intercom.service';

// Import models
import { Team, TeamMember, TeamPhone, TeamGroup, TeamMetaData, TeamCustomField } from '../models/team-model';

// Import lodash for compatibility with original code
import * as _ from 'lodash';

// Interface for filter observables
export interface FilterOptions {
  orderBy?: 'firstName' | 'lastName' | 'startDate';
  direction?: 'asc' | 'desc';
  groupIdFilter?: string; // valid groupId
  searchString?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TeamDataService implements OnDestroy {

  // Public service to tell us which data to load
  public service: string = '';
  
  // Public basic flag to trigger setup actions, hide functionality
  public basicMode: boolean = false;
  public setupOpen: boolean = false; // need to not open twice upon refresh etc
  public intercomTokens: any = {};
  public emailImpersonate: string = '';
  // Reason: serialized copy of the last intercom update sent, so we only call the server when the
  // values actually change rather than on every team snapshot
  private lastIntercomSignature: string = '';
  
  // Language and app configurations
  public languages: any[] = [
    { label: 'English (US)', code: 'en' },
    { label: 'English (UK)', code: 'en-gb' },
    { label: 'Español', code: 'es' },
    { label: 'Portugese', code: 'pt-br' },
    { label: 'Français', code: 'fr' },
    { label: 'Pу́сский', code: 'ru' },
    { label: '日本語', code: 'ja' },
  ];
  
  public emailApps: any[] = [
    { label: 'Gmail', code: 'gmail' },
  ];
  
  public smsApps: any[] = [
    // { label: 'Google Chat', code: 'gchat' },
    // { label: 'Google Hangouts', code: 'hangouts' },
    { label: 'WhatsApp', code: 'whatsapp' },
  ];
  
  public phoneApps: any[] = [
    { label: 'DialPad', code: 'dialpad' },
    { label: 'RingCentral', code: 'ringcentral' },
  ];

  // Use non-observables to ease filtering and caching, treat same as stored
  public team: Team = new Team(); // initialize to avoid errors with missing arrays, etc

  // Add convenience accessors 
  get metaData(): TeamMetaData {
    return this.team.metaData;
  }
  
  get logoUrl(): string {
    return this.team.logoUrl;
  }
  
  get members(): TeamMember[] {
    return this.team.members;
  }
  
  get groups(): TeamGroup[] {
    return this.team.groups;
  }

  // Subscription to update upon cache change
  protected teamObservable: Observable<any> | null = null;
  protected teamSubscription: Subscription | null = null;
  private firebaseListenerCleanup?: () => void;

  // Separate filtered members array, updated using shallow copy upon order / filter/ page actions
  public filteredMembers: TeamMember[] = [];
  public orderBy: string = 'lastName'; // any valid field of TeamMember; public since set in preferences
  protected direction: string = 'asc'; // asc or desc, for use in _.orderBy
  protected groupIdFilter: string = ''; // blank string or valid group ID
  
  get filterGroupId(): string {
    return this.groupIdFilter;
  }
  
  private _searchString: string = '';
  
  get searchString(): string {
    return this._searchString;
  }
  
  set searchString(value: string) {
    this._searchString = value;
  } 
  public filter: ReplaySubject<FilterOptions> = new ReplaySubject();  

  // Preferences (also see orderBy above)
  public emailApp: string = 'default';
  public smsApp: string = 'default';
  public phoneApp: string = 'default';

  // Status observables
  public filtering: BehaviorSubject<boolean> = new BehaviorSubject(false);  
  public refreshing: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public serverDataReady: ReplaySubject<boolean> = new ReplaySubject();
  
  // Global change detection observable - replaces AngularFire2's automatic change detection
  public teamUpdated$: Subject<void> = new Subject<void>();
  
  // Cache operations
  protected cacheKey: string = '';
  protected firebaseUid: string = ''; // must use this ID to access user table for permissions
  protected cacheToken: string = ''; // must use this to log into firebase instead of google token

  // Timer for session expiration
  private expirationTimer: any;
  public readonly EXPIRED_SESSION_MSG = 'Your TeamTools session has expired! Please log in again.';

  constructor(
    private translate: TranslateService,
    private user: UserService,
    private api: ApiService,
    private utils: UtilsService,
    private googleAuth: GoogleAuthService,
    private ngZone: NgZone,
    private firebase: FirebaseService,
    private intercom: IntercomService
  ) {
    this.utils.logDebug('TeamDataService constructor called');
    this.clearFilterParams();

    // Upon user login, load data
    this.user.loggedIn.subscribe((service: string) => {
      this.utils.logDebug('user loggedIn received, calling load');      
      this.load(service);  
    });

    // Upon user logout, clean up
    this.user.loggedOut.subscribe((service: string) => {
      this.utils.logDebug('teamdata received user logout event with service '+service);
      if (service !== 'demo') {
        if (this.teamSubscription) {
          this.teamSubscription.unsubscribe(); 
        }
        // Clean up Firebase listener and sign out
        if (this.firebaseListenerCleanup) {
          this.firebaseListenerCleanup();
          this.firebaseListenerCleanup = undefined;
        }
        // Sign out from Firebase if signed in
        if (this.firebaseUid) {
          // If impersonating, clear Firebase so subsequent login does not get wrong subscriber info
          if (this.emailImpersonate !== '') {
            this.utils.logDebug('Clearing team_id in Firebase for user id ' + this.firebaseUid);
            this.firebase.setData('/users/' + this.firebaseUid, { team_id: '' })
              .then(() => this.firebase.signOut())
              .catch(error => {
                this.utils.logError('Failed to clear Firebase data', error);
                this.utils.handleError(error);
              });
          } else {
            this.firebase.signOut().catch(error => {
              this.utils.logError('Firebase sign out failed', error);
              this.utils.handleError(error);
            });
          }
          this.firebaseUid = ''; // To stop autologin upon auth change
        }
        this.deleteLocalData();
     }
      this.team.members = [];
      this.team = new Team();
      this.clearFilterParams();
      // Reset loading state on logout to prevent UI issues
      // when exiting demo mode before data finishes loading
      this.loading.next(false);
      this.refreshing.next(false);
    });

    // Upon refresh ending, set timeout if session expiration set 
    this.refreshing.subscribe((refreshing: boolean) => {
      let sessionMinutes: number = this.team.metaData.subscriptionData.sessionMinutes;
      if (!refreshing && !_.isUndefined(sessionMinutes) &&  sessionMinutes > 0) {
        // prep parameters
        let sessionMs = Math.trunc(sessionMinutes) * 60 * 1000;
        let loggedInTime = this.googleAuth.loggedInTime;
        if (!loggedInTime || loggedInTime <= 0) {
          loggedInTime = Date.now(); // if missing from stored obj, set login time to now
        }
        let msUntilExpiry: number = loggedInTime + sessionMs - Date.now(); 
        let maxMsTimeout = 2000000000; // about 24 days; if higher integer overflow makes setTimeout get called immediately
        msUntilExpiry = msUntilExpiry > maxMsTimeout ? maxMsTimeout : msUntilExpiry; // reduce to max if higher
        msUntilExpiry = msUntilExpiry < 10000 ? 10000 : msUntilExpiry; // minimum 10 secs so app loads before logout
        
        // Log out user when session is expired
        this.utils.logDebug('Expiring session in '+msUntilExpiry+' ms');
        if (this.expirationTimer) {
          clearTimeout(this.expirationTimer);
        }
        this.expirationTimer = setTimeout(() => { 
          this.utils.logDebug('Expired session, logging user out');
          this.utils.handleError(this.EXPIRED_SESSION_MSG); 
          this.user.logout();          
        }, msUntilExpiry); // in milliseconds 
      }
    });

    // Upon calls to filter, read data and filter arrays
    // no need to debounce, the ion-search already does it
    this.filter.subscribe((filterOptions: FilterOptions) => {
      for (let option in filterOptions) {
        (this as any)[option] = (filterOptions as any)[option]; // interface members map to private members here
      }
      this.updateFilteredMembers(filterOptions);
    });

    // Set preferences from storage
    this.loadPreferencesFromStorage();
  }

  // Load user preferences from storage
  private async loadPreferencesFromStorage(): Promise<void> {
    try {
      this.orderBy = await this.utils.getFromStorage('sortOrder', 'lastName');
      this.emailApp = await this.utils.getFromStorage('emailApp', 'default');
      
      let smsApp = await this.utils.getFromStorage('smsApp', 'default');
      // Handle deprecated SMS apps
      smsApp = smsApp === 'gchat' ? 'default' : smsApp === 'hangouts' ? 'default' : smsApp;
      this.smsApp = smsApp;
      
      this.phoneApp = await this.utils.getFromStorage('phoneApp', 'default');
    } catch (error) {
      this.utils.logError('Error loading preferences from storage', error);
    }
  }

  // Since this is a singleton that exists as long as the page does, no need to clean up, but just in case
  ngOnDestroy(): void {
    if (this.teamSubscription) {
      this.teamSubscription.unsubscribe();
    }
    if (this.expirationTimer) {
      this.utils.logDebug('clearing expiration timeout');
      clearTimeout(this.expirationTimer);      
    }
  } 

  // Clear filter parameters to default state
  protected clearFilterParams(): void {
    this.filteredMembers = [];
    this.direction = 'asc'; // asc or desc, for use in _.orderBy
    this.groupIdFilter = ''; // blank string or valid group ID
    this._searchString = '';     
  }

  // Method to populate filteredMembers upon new data or changed filters 
  protected updateFilteredMembers(newFilter?: FilterOptions): void {
    this.utils.logDebug('updateFilteredMembers called with newFilter = ', newFilter);
    this.filtering.next(true);

    let newFilteredMembers: TeamMember[];

    // Clone members (they are sorted upon data load, and further in each view, but we now do here since sorting is a pref)
    newFilteredMembers = _.clone(this.team.members);
    let secondOrdering = this.orderBy === 'firstName' ? 'lastName' : 'firstName';
    newFilteredMembers = _.orderBy(newFilteredMembers, [this.orderBy, secondOrdering], [this.direction as 'asc' | 'desc', this.direction as 'asc' | 'desc']); 

    // Filter by group if set 
    if (this.groupIdFilter) {
      newFilteredMembers = newFilteredMembers.filter((member: TeamMember) => {
        return _.includes(member.groupIds, this.groupIdFilter) || member.organization === this.groupIdFilter;
      });
    }      

    // Apply search terms
    if (!newFilter || this._searchString) {
      if (this._searchString && this._searchString.trim() != '') {
        let searchWords: string[] = _.words(this._searchString);
        searchWords.forEach((word: string) => {
          newFilteredMembers = newFilteredMembers.filter((member: TeamMember) => {
            let matched: boolean = _.some(Object.keys(member), (key: string) => {
              let fieldsToSkip = [
                'id',
                'photoUrl',
                'photoCacheUrl',
                'photoData',
                'photoFormat',
                'isAdmin',
                't2Admin',
                't2Owner',
                'managerId',
                'managerName',
                'managerEmail',
                'customDirectoryId',
              ];
              if (_.isString((member as any)[key]) && !_.includes(fieldsToSkip, key)) {
                if ((member as any)[key].toLowerCase().includes(word.toLowerCase())) { 
                  this.utils.logDebug('matched field = '+key);
                  return true;
                }
              }
              return false; 
            });
            
            // Now search phones and customFields
            if (!matched) {
              matched =  _.some(member.phones, (phone: TeamPhone) => {
                let searchFor: string = TeamDataService.onlyAlphaNum(word);
                let phoneNum: string = TeamDataService.onlyAlphaNum(phone.val);
                if (phoneNum) {
                  if (phoneNum.includes(searchFor)) {
                    this.utils.logDebug('matched field = phone '+phoneNum);
                    return true;
                  }
                }
                return false;
              });
            }
            if (!matched) {
              matched =  _.some(member.customFields, (cfield: TeamCustomField) => {
                if (_.isString(cfield.val)) {
                  if (cfield.val.toLowerCase().includes(word.toLowerCase())) {
                    this.utils.logDebug('matched field = custom field '+cfield.name);
                    return true;
                  }
                }
                return false;
              });
            }
            return matched;
          });
        });
      }  
    }

    // Make sure array is still there
    if (!newFilteredMembers) {
      newFilteredMembers = [];
    }
    
    // Update UI array
    // Apparently we are sometimes outside the angular zone, so have to explicitly put the update in ngZone so change detection works
    this.ngZone.run(() => {
      this.filteredMembers = newFilteredMembers;
    });
    
    this.filtering.next(false);
  }
  
  // Called upon login or demo mode 
  public async load(service: string): Promise<string> {
    this.utils.logInfo('team load called');

    // try catch this entire thing 
    // still doesn't work for firebase permission errors: see
    //   http://stackoverflow.com/questions/41650464/in-firebase-can-client-know-the-reason-when-getting-permission-deny?noredirect=1&lq=1
    //   https://github.com/angular/angularfire2/issues/816

    // Set flag
    this.loading.next(true);

    try {
      // Set service so we don't have to pass it around
      this.service = service;
  
      // First get rid of any old subscription
      if (this.teamSubscription) {            
        this.teamSubscription.unsubscribe();  
      }

      // Load local data and call refresh in parallel
      const promiseArray: Promise<any>[] = [];
      promiseArray.push(this.loadLocalData());
      promiseArray.push(this.refresh());

      // Upon return populate non-member data, and replace local member list with firebase, and write to local storage
      await Promise.all(promiseArray);
      await this.getCacheSubscriptions();

      // Set up Firebase real-time subscription with debouncing and change detection
      if (this.teamObservable) {
        this.teamSubscription = this.teamObservable
          .pipe(
            debounceTime(500),
            distinctUntilChanged((old: any, fresh: any) => {
              return _.isEqual(old, fresh);
            })
          )
          .subscribe((snapshot: any) => {
            this.utils.logInfo('team update received');
            this.utils.logDebug('team update snapshot: ', snapshot);
            
            if (!_.has(snapshot, 'members') || snapshot.members.length === 0) {
              this.utils.logInfo('team update empty, only updating metadata');
              if (!_.has(snapshot, 'metaData') || snapshot.metaData === '') {
                this.utils.logInfo('team update metadata empty too, ending load and refresh');
                this.loading.next(false); 
                this.refreshing.next(false); 
                return;
              }  
              this.team.metaData = snapshot.metaData;
              return;
            }

            this.ngZone.run(() => {
              // Get refreshing flag from Firebase
              let refreshing: boolean = _.has(snapshot, 'refreshing') ? snapshot.refreshing : false;
              this.utils.logDebug('team is refreshing: ' + (refreshing ? 'true' : 'false'));
              
              // Clone Firebase data since raw snapshot used above for comparison in distinctUntilChanged
              this.team = _.cloneDeep(snapshot); 
              
              // Must turn objects into arrays (which orderBy does) and sort them
              this.team.members = _.orderBy(this.team.members, [this.orderBy], [this.direction as 'asc' | 'desc']) || []; 
              this.fixManagerNames(); // Fix manager name ordering for Japanese
              this.updateFilteredMembers();
              this.team.hiddenMembers = _.orderBy(this.team.hiddenMembers, [this.orderBy], [this.direction as 'asc' | 'desc']) || []; 
              this.team.groups = _.orderBy(this.team.groups, 'name') || []; 
              this.team.hiddenGroups = _.orderBy(this.team.hiddenGroups, 'name') || []; 
              this.team.truncatedGroups = _.orderBy(this.team.truncatedGroups, 'name') || []; 
              this.updateTeam();
              
              // Emit team update for global change detection (replaces AngularFire2 behavior)
              this.teamUpdated$.next();
              
              // If this is the last update, loading and refreshing are done
              if (!refreshing) {
                this.loading.next(false); 
                this.refreshing.next(false); 
              }
              
              // Update Intercom with new data
              this.updateIntercom();
            });  

            // Store updated data locally
            this.storeLocalData();
          });
      }
      
      return 'load success';
    } catch (error) {
      if (_.includes(JSON.stringify(error), 'cannot use apis')) {
        this.loading.next(false);
        return 'no api access';              
      } else if (this.utils.offline) {
        this.utils.logInfo('started in offline mode');
        this.utils.errorSubject.next('offline mode'); 
      } else {
        this.utils.handleError('error on load'); 
      }
      this.loading.next(false);
      return 'promise rejection on load';    
    }
  }

  // Public method to refresh team data
  public async refresh(force?: boolean): Promise<string> {
    this.utils.logInfo('refresh called');
    if (_.isUndefined(force)) { force = false; }
    
    if (this.service !== 'google' && this.service !== 'demo') { // add microsoft later
      throw new Error('refresh called with bad service = '+this.service); 
    }
    
    if (this.utils.offline) {
      throw new Error('Cannot refresh while offline!');       
    }
    
    // Set flag for spinner only if force
    if (force) { 
      this.refreshing.next(true);      
    }

    try {
      // Get tokens and email 
      let tokensPromise: Promise<any>;
      if (this.service === 'google') {
        if (force) {
          tokensPromise = this.googleAuth.refreshTokens();
        } else {
          tokensPromise = Promise.resolve('no token refresh needed');        
        }
      } else {
        tokensPromise = Promise.resolve('skipping token refresh for non-google service');
      }
      
      await tokensPromise;
      
      let idToken: string = '';
      let accessToken: string = '';
      if (this.service === 'google') {
        idToken = this.googleAuth.idToken;
        accessToken = this.googleAuth.accessToken;
      }
      
      const callParams: any = {
        force: force,
        email: this.user.email, 
        user_id: this.user.id,
        id_token: idToken,
        access_token: accessToken,
        service: this.service,
        emailImpersonate: this.emailImpersonate
      };

      // For initial load send name so server can create free sub for new users
      if (!force) {
        callParams.firstName = this.user.firstName;
        callParams.lastName = this.user.lastName;
      }

      // Set up and make call to server
      const res: any = await this.api.t2Server('cache/refresh', callParams);
      
      // If the API returned a successful response, set metaData and cache key
      if (_.has(res, 'metaData')) {
        this.team.metaData = res.metaData;
        this.cacheKey = this.getCacheTeamKeyFromSubId(res.metaData.subscriptionData.cbId);
        this.cacheToken = res.cacheToken;
        this.intercomTokens = res.intercomTokens;
        this.updateTeam(); // from metaData: basic mode, language
        this.serverDataReady.next(true);
        this.utils.logInfo('refresh call successful, status: '+res.status);  
        
        if (res.status === 'could not refresh') {
          this.loading.next(false); 
          this.refreshing.next(false); 
        }  
        return 'success';
      } else {
        this.utils.logInfo('refresh call failed, status: ' + (res && typeof res === 'object' && 'status' in res ? (res as any).status : 'response missing'));    
        throw new Error('refresh returned no metaData');
      }  
    } catch (error) {
      // If no admin email and user email gets Not Authorized response, APIs are off (I think)
      if (_.includes(JSON.stringify(error), 'cannot use apis') || 
          (_.includes(JSON.stringify(error), 'Not Authorized to access this resource') && this.emailImpersonate === '')) { // to avoid false no api access error when impersonating
        this.team.metaData.permission = 'no api access';
        this.basicMode = true; 
      }    
      this.serverDataReady.next(true);
      this.refreshing.next(false);
      this.utils.logError('refresh call error', error);    
      throw error;
    }
  }  
  
  // Get cache subscriptions and set up Firebase listeners
  private async getCacheSubscriptions(): Promise<string> {
    let path: string = '';
    let pathPromise: Promise<string>;

    if (this.service === 'demo') {
      this.utils.logDebug('getCacheSubscriptions called in demo mode');
      path = '/teams/demo';
      pathPromise = Promise.resolve('path fixed in demo mode');
    } else {
      this.utils.logDebug('getCacheSubscriptions called while logged in');
      pathPromise = this.firebaseSignIn().then(() => {
        // Now make sure this user has an entry in users with cache key so they can access data
        const firebaseUid = this.firebase.getFirebaseUid();
        if (firebaseUid) {
          this.firebaseUid = firebaseUid;
          return this.firebase.setData('/users/' + firebaseUid, { team_id: this.cacheKey });
        }
        throw new Error('No Firebase UID available');
      }).then(() => {
        path = '/teams/' + this.cacheKey;
        return 'Firebase setup complete';
      });
    }

    return pathPromise.then(() => {
      // Set up real-time listener for team data
      this.setupFirebaseListener(path);
      this.utils.logInfo('Cache subscriptions setup with Firebase listener');
      return 'logged in';
    }).catch(error => {
      this.utils.logError('Failed to set up Firebase subscription', error);
      this.utils.handleError(error);
      throw error;
    });
  }

  // Firebase sign-in with custom token
  private async firebaseSignIn(): Promise<void> {
    // Initialize Firebase if not already done
    if (!this.firebase.isInitialized()) {
      this.firebase.initialize();
    }

    // Check if already signed in and validate token for security
    const currentUser = this.firebase.getCurrentUserSync();
    if (currentUser) {
      // Security check: verify current Firebase token matches our fresh cacheToken
      // This prevents unauthorized access if user switched accounts/teams
      try {
        const currentIdToken = await this.firebase.getCurrentUserIdToken();
        
        if (currentIdToken && currentIdToken === this.cacheToken) {
          // Token matches - user is properly authenticated for this team
          this.firebaseUid = currentUser.uid;
          this.utils.logInfo('User already signed in to Firebase with valid token');
          return;
        } else {
          // Token doesn't match or failed to retrieve - need to re-authenticate for security
          this.utils.logInfo('Firebase token mismatch - re-authenticating for security');
        }
      } catch (error) {
        // If token retrieval fails, re-authenticate for safety (matches old code behavior)
        this.utils.logInfo('Failed to retrieve current Firebase token - re-authenticating for security');
        this.utils.logError('Token retrieval error', error);
      }
    }

    // Sign in with custom token from backend
    if (!this.cacheToken) {
      throw new Error('No cache token available for Firebase sign-in');
    }

    try {
      const user = await this.firebase.signInWithCustomToken(this.cacheToken);
      this.firebaseUid = user.uid;
      this.utils.logInfo('User signed into Firebase');
    } catch (error) {
      this.utils.logError('Firebase sign-in failed', error);
      this.utils.handleError(error);
      throw error;
    }
  }

  // Set up Firebase real-time listener for team data and create Observable
  private setupFirebaseListener(path: string): void {
    // Clean up existing listener if any
    if (this.firebaseListenerCleanup) {
      this.firebaseListenerCleanup();
    }

    // Create Observable from Firebase listener for compatibility with old subscription pattern
    this.teamObservable = new Observable(subscriber => {
      // Set up Firebase listener that feeds the Observable
      this.firebaseListenerCleanup = this.firebase.listenToData(path, (data) => {
        if (data) {
          subscriber.next(data); // Emit Firebase data to Observable subscribers
        }
      });

      // Return cleanup function for Observable
      return () => {
        if (this.firebaseListenerCleanup) {
          this.firebaseListenerCleanup();
          this.firebaseListenerCleanup = undefined;
        }
      };
    });
  }

  // Update team-related settings
  public updateTeam(): void {
    this.updateBasicMode(this.team.metaData);
    this.updateDefaultLanguage(this.team.metaData.subscriptionData.defaultLanguage);
  }

  // Update basic mode flag based on permissions
  public updateBasicMode(metaData: TeamMetaData): void {
    this.basicMode =  metaData.permission !== 'full' && 
                      metaData.permission !== 'demo'; 
  } 

  // Update default language setting
  public updateDefaultLanguage(defaultLanguage: string): void {
    let defaultLang = defaultLanguage;
    if (!defaultLang || defaultLang === '' || defaultLang === 'default') {
      defaultLang = 'en';
    }  
    
    this.utils.getFromStorage('language', 'default').then((localLang: string) => {
      if (!localLang || localLang === '' || localLang === 'default') {
        this.translate.use(defaultLang);
        this.utils.logDebug('updating to default language '+defaultLang);
      } else {
        this.utils.logDebug('not updating to default language '+defaultLang+' since local language is set');
      }
    });
  }

  // Load data from local storage
  private async loadLocalData(): Promise<string> {
    try {
      this.utils.logInfo('loading locally stored team data'); 
      const encodedTeam: string = await this.utils.getFromStorage(this.service+'-team', '');
      
      let team: any = null;
      if (encodedTeam && encodedTeam !== '') {
        team = this.utils.decodeObj(encodedTeam);
      }
      
      this.utils.logDebug('local team', team);      
      
      // If we have stored data, get rid of the spinner now, updates from refresh will pop up when ready
      if (team && team.members && team.members.length > 0) {
        this.team = team;
        this.updateTeam(); 
        (this as any).updateFilteredMembers();
        this.loading.next(false);        
      } else {
        this.utils.logInfo('local team data empty');
      }     
      return 'success';
    } catch (error) {
      this.utils.logInfo('local team data not found');
      return 'local team data not found';
    }
  }

  // Store data to local storage
  private async storeLocalData(): Promise<void> {
    try {
      // Store the team object as an encoded string
      const encodedTeam = this.utils.encodeObj(this.team);
      await this.utils.writeToStorage(this.service+'-team', encodedTeam);
    } catch (reason: any) {
      this.utils.logInfo('error storing local data', reason);    
    }
  }

  // Delete local storage data
  private async deleteLocalData(): Promise<void> {
    try {
      await this.utils.writeToStorage(this.service+'-team', '');
    } catch (reason: any) {
      this.utils.logInfo('error deleting local data', reason);    
    }
  }

  // Convert subscription ID to cache key format
  public getCacheTeamKeyFromSubId(cbId: string): string {
    return cbId.replace(/\./g, ',');
  }

  // Get shortened ID from email
  public getCbIdFromEmail(email: string): string {
    return email.substring(0, 47); // 50 char max, allowing for potential url encoding
  }

  // Static utility to extract only alphanumeric characters
  public static onlyAlphaNum(input: string): string {
    return input.replace(/[^0-9a-z]/gi, '');
  }   

  // Get top phones for a member (formatted for display)
  public getTopPhones(member: TeamMember): TeamPhone[] {
    if (!_.isArray(member.phones)) {
      return [];
    }
    let phones = member.phones;
    let topPhones: TeamPhone[] = [];

    // Turn the type into the first letter plus an incremented number (e.g. m, w, h, m2, w2)
    let typeCounts: { [key: string]: number } = {};
    phones.forEach((phone: TeamPhone) => {
      // Skip fax numbers
      if (!_.has(phone, 'type') || phone.type === 'fax' || !phone.val || phone.val === '') {
        return;
      }
      let newType: string = phone.type.charAt(0);
      if (_.has(typeCounts, newType)) {
        typeCounts[newType]++;
        newType = newType + typeCounts[newType];
      } else {
        typeCounts[newType] = 1;
      }
      topPhones.push(new TeamPhone(newType, phone.val));
    });

    // Now pick out top 3: mobile first, then work, then home
    _.sortBy(topPhones, [function(phone: TeamPhone) { 
      if (phone.type[0] === 'm') {
        return '0'+phone.type;
      } else if (phone.type[0] === 'w') {
        return '1'+phone.type;
      } else if (phone.type[0] === 'h') {
        return '2'+phone.type;
      } else {
        return phone.type;
      }
    }]);

    return topPhones;  
  }

  // Helper methods to find team data by ID
  public getGroupById(id: string): TeamGroup | undefined {
    return _.find(this.groups, ['id', id]);
  }

  public getOrgById(id: string): boolean {
    return _.includes(this.team.orgs, id);
  }

  public getMemberById(id: string): TeamMember | undefined {
    return _.find(this.members, ['id', id]);
  }

  public getMemberByEmail(email: string): TeamMember | undefined {
    return _.find(this.members, ['email', email]);
  }

  // Get member status color
  public getMemberStatusColor(member: TeamMember): string | null {
    if (member && member.statusColor && member.statusColor !== '') {
      return member.statusColor;
    } else {
      return null;
    }
  }

  // Convert status color names to CSS color values for status dots
  public getStatusColorValue(colorName: string | null): string {
    if (!colorName || colorName === '') return 'transparent';

    const colorMap: { [key: string]: string } = {
      'statusGreen': 'var(--t2-status-green-rgba)',
      'statusOrange': 'var(--t2-status-orange-rgba)',
      'statusRed': 'var(--t2-status-red-rgba)',
      'disabled': '#d3d3d3',
      'transparent': 'rgba(0, 0, 0, 0)'
    };

    return colorMap[colorName] || colorName;
  }

  // Format full name based on language
  public getFullName(firstName: string, lastName: string): string {
    if (this.translate.instant('LANG') === 'ja') {
      return lastName+' '+firstName;
    } else {
      return firstName+' '+lastName;
    }
  }

  // Get current user's status color
  public getUserStatusColor(): string | null {
    const member = this.getMemberById(this.user.id);
    return member ? this.getStatusColorValue(this.getMemberStatusColor(member)) : null;
    
  }

  // Get member email from partial email string
  // copied from old codebase providers/team-data.ts lines 1154-1173
  public getMemberEmailFromPartial(partial: string): string {
    let ret: string = '';
    let loweredPartial = _.toLower(partial);
    _.forEach(this.members, (member: TeamMember) => {
      // return even if partial has extra chars at end
      let matched: boolean = _.startsWith(_.toLower(member.email), loweredPartial.substring(0, member.email.length));
      if (matched) {
        if (ret === '') {
          ret = member.email;
          return true; // continue
        } else {
          // two found, so return blank and break
          ret = '';
          return false; // break
        }
      }
      return true; // continue
    });
    // this.utils.logDebug('getMemberEmailFromPartial = '+ret+' for partial '+partial);
    return ret;
  }

  // Check if current user is admin
  public isUserAdmin(): boolean {
    if (this.service === 'demo') {
      return false;
    }
    let userMember = this.getMemberById(this.user.id);
    if (!userMember) {
      userMember =  _.find(this.team.hiddenMembers, ['id', this.user.id]);      
    }
    if (userMember && _.has(userMember, 'isAdmin') && userMember.isAdmin === true) {
      return true;
    } else if (userMember && _.has(userMember, 't2Admin')) {
      return userMember.t2Admin;      
    } else {  
      return false;
    }
  }

  // Check if current user is owner
  public isUserOwner(): boolean {
    if (this.service === 'demo') {
      return false;
    }
    if (this.metaData.subscriptionData.ownerEmail === this.user.email) {
      return true;
    }
    let userMember: TeamMember | undefined = this.getMemberById(this.user.id);
    if (!userMember) {
      userMember =  _.find(this.team.hiddenMembers, ['id', this.user.id]);      
    }
    if (userMember && _.has(userMember, 't2Owner')) {
      return userMember.t2Owner;
    } else {
      return false;
    }
  }

  // Check if support should be hidden for this user
  public isSupportHidden(): boolean {
    let ret: boolean;
    if (this.metaData.subscriptionData.support === 'NONE') {
      ret = true;
    } else if (this.metaData.subscriptionData.support === 'LIMITED') {
      ret = !(this.isUserAdmin() || this.isUserOwner());
    } else if (this.metaData.subscriptionData.support === 'unknown') {
      ret = true; // not loaded yet 
    } else {
      ret = false;
    }
    return ret;
  }

  // Check if partner branding is configured
  public isPartnerSet(): boolean {
    let ret: boolean = false;
    if (_.has(this.metaData.subscriptionData, 'partner') &&
        _.isString(this.metaData.subscriptionData.partner) &&
        this.metaData.subscriptionData.partner !== '' &&
        this.team.partnerUrls.logo !== '' && 
        _.startsWith(this.team.partnerUrls.logo, 'http') && 
        this.team.partnerUrls.link !== '' && 
        _.startsWith(this.team.partnerUrls.link, 'http')) {
      ret = true;
    }
    return ret;
  }

  // TODO: Implement public addDynamicPhoto
  // See original codebase for details

  // Generate email links for different email apps
  // to generate mailto links use http://mikemustaine.com/mmtools/files/mailTo.html
  // https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1&su=ionic&to=adam@amrsj.com
  // for config.xml see https://stackoverflow.com/questions/39125043/getting-neterr-unknown-url-scheme-while-calling-telephone-number-with-ionic
  
  public getEmailLink(recipient: string, subject?: string, body?: string): string {
    subject = _.isUndefined(subject) ? '' : subject;
    body = _.isUndefined(body) ? '' : body;
    let ret: string = '';
    
    if (this.emailApp === 'gmail') {
      if (this.utils.platform === 'ios' || this.utils.platform === 'android' || this.utils.platform === 'web-ios') {
        ret = 'googlegmail:///co?&to='+recipient+'&subject='+subject+'&body='+body;
      } else {
        ret = 'https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1&to='+recipient+'&su='+subject+'&body='+body;
      }  
    } else {
      ret = 'mailto:'+recipient+'?subject='+subject+'&body='+body;
    }  
    return ret;
  }

  // Generate SMS links for different SMS apps
  public getSmsLink(recipient: string, id: string): string {
    let ret: string = '';
    if (this.smsApp === 'whatsapp') {
      // ret = 'https://wa.me/'+recipient;
      ret = 'whatsapp://send?phone='+recipient+'&abid='+recipient;
    } else if (this.smsApp === 'gchat') {
      ret = 'https://chat.google.com/u/1/dm/users/'+id;
    } else if (this.smsApp === 'hangouts') {
      ret = 'https://hangouts.google.com/chat/person/'+id;
    } else {
      // needed for ios, breaks web and android
      // if (this.utils.platform === 'ios' || this.utils.platform === 'web-ios') {
      //   ret = this.sanitizer.bypassSecurityTrustUrl('sms:'+recipient);
      // } else {
        ret = 'sms:'+recipient;
    }
    return ret;
  }

  // Generate phone links for different phone apps
  public getPhoneLink(recipient: string): string {
    let ret: string = '';
    if (this.phoneApp === 'dialpad') {
      // https://help.dialpad.com/hc/en-us/articles/115001792066-Enable-Click-to-Call-on-Third-Party-Apps
      // this works but opens safari with a modal to open dialpad; better to just open and blame them
      // if (this.utils.platform === 'ios' || this.utils.platform === 'web-ios') {
      //   ret = 'https://dialpad.com/launch?phone='+recipient;
      // } else {
        ret = 'dialpad://'+recipient;
      // }
    } else if (this.phoneApp === 'ringcentral') {
      ret = 'rcmobile://call?number='+recipient;
    } else {
      ret = 'tel:'+recipient;
    }
    return ret;
  }

  // Edit subscription data
  public async editSubscription(fieldName: string, oldValue: string, newValue: string, createSub?: string): Promise<string> {
    this.utils.logInfo('editSubscription called');
    if (this.service !== 'google') { // add microsoft later
      throw new Error('editSubscription called with bad service = '+this.service); 
    }

    try {
      // Get tokens and email 
      let tokensPromise: Promise<any>;
      if (this.service === 'google') {
        tokensPromise = this.googleAuth.refreshTokens();
      } else {
        tokensPromise = Promise.resolve('skipping token refresh');
      }
      
      await tokensPromise;
      
      let idToken: string = '';
      let accessToken: string = '';
      if (this.service === 'google') {
        idToken = this.googleAuth.idToken;
        accessToken = this.googleAuth.accessToken;
      }

      // Set up and make call to server
      const res: any = await this.api.t2Server('subscription/edit', {
        email: this.user.email, 
        id_token: idToken,
        access_token: accessToken,
        service: this.service,
        cachePath: this.utils.cacheUrl+'/teams/'+this.cacheKey+'/metaData/subscriptionData/'+fieldName,
        oldVal: oldValue,
        newVal: newValue,
        createSub: createSub
      });
      
      if (!!res && res.status === 'success') {
        this.utils.logInfo('editSubscription call successful');    
        return 'success';
      } else {
        this.utils.logInfo('editSubscription call failed, status: ' + (!!res ? res.status : 'response missing'));    
        throw new Error('editSubscription returned bad status');
      }  
    } catch (error) {  
      this.utils.logError('editSubscription call error', error);    
      throw error;
    }
  }  
  
  // Create subscription (now rarely called unless initial load doesn't create the sub as it should)
  public createSubscription(adminEmail: string): Promise<string> {
    const createSubParams: any = {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      numUsers: this.team.metaData.untruncatedNumMembers
    };
    const createSubParamsString = JSON.stringify(createSubParams);
    return this.editSubscription('adminEmail', '', adminEmail, createSubParamsString);
  }

  // Get account URL from API
  public getAccountUrl(): Promise<string> {
    return this.getResourceFromApi('accountUrl');
  }

  // Get checkout URL with plan details
  public getCheckoutUrl(planId: string, planQuantity: number): Promise<string> {
    const params: any = {
      planId: planId,
      planQuantity: planQuantity
    };
    return this.getResourceFromApi('checkoutUrl', params);
  }

  // Get full hosted page object for client-side Chargebee.js checkout overlay
  public getCheckoutHostedPage(planId: string, planQuantity: number): Promise<any> {
    const params: any = {
      planId: planId,
      planQuantity: planQuantity,
      clientSide: true
    };
    return this.getResourceFromApi('checkoutUrl', params);
  }

  // Get full portal session object for client-side Chargebee.js portal overlay
  public getPortalSession(): Promise<any> {
    return this.getResourceFromApi('accountUrl', { clientSide: true });
  }

  // Get resource from API
  public async getResourceFromApi(name: string, params?: any): Promise<string> {
    this.utils.logInfo('getResourceFromApi called, name: ' + name);
    if (this.service !== 'google') { // add microsoft later
      throw new Error('getResourceFromApi called with bad service = '+this.service); 
    }

    try {
      // Get tokens and email 
      let tokensPromise: Promise<any>;
      if (this.service === 'google') {
        tokensPromise = this.googleAuth.refreshTokens();
      } else {
        tokensPromise = Promise.resolve('skipping token refresh');
      }
      
      await tokensPromise;
      
      let idToken: string = '';
      let accessToken: string = '';
      if (this.service === 'google') {
        idToken = this.googleAuth.idToken;
        accessToken = this.googleAuth.accessToken;
      }

      // Set up call to server
      const callParams: any = {
        email: this.user.email, 
        id_token: idToken,
        access_token: accessToken,
        service: this.service,
        name: name        
      };
      if (params) {
        _.assign(callParams, params);
      }

      // Make call to server
      const res: any = await this.api.t2Server('resource/get', callParams);
      
      if (!!res && res.status === 'success') {
        this.utils.logInfo('resource get call successful');    
        return res.resource;
      } else {
        this.utils.logInfo('resource get call failed, status: ' + (!!res ? res.status : 'response missing'));    
        throw new Error('resource get returned bad status');
      }  
    } catch (error) {  
      this.utils.logError('resource get call error', error);    
      throw error;
    }
  }    

  // Edit user data
  public async editUser(userId: string, fieldName: string, oldValue: string, newValue: string, metaData?: string, index?: string): Promise<string> {
    this.utils.logInfo('editUser called');
    if (this.service !== 'google') { // add microsoft later
      throw new Error('editUser called with bad service = '+this.service); 
    }

    try {
      // Get tokens and email 
      let tokensPromise: Promise<any>;
      if (this.service === 'google') {
        tokensPromise = this.googleAuth.refreshTokens();
      } else {
        tokensPromise = Promise.resolve('skipping token refresh');
      }
      
      await tokensPromise;
      
      let idToken: string = '';
      let accessToken: string = '';
      if (this.service === 'google') {
        idToken = this.googleAuth.idToken;
        accessToken = this.googleAuth.accessToken;
      }

      let cachePath: string;
      if (fieldName === 'phones') {
        cachePath = this.utils.cacheUrl+'/teams/'+this.cacheKey+'/members/'+userId+'/phones';        
      } else if (metaData === 'customField') {
        // refresh and customField editing now inserts blanks, so this is ok
        cachePath = this.utils.cacheUrl+'/teams/'+this.cacheKey+'/members/'+userId+'/customFields/'+index+'/val';        
      } else {
        cachePath = this.utils.cacheUrl+'/teams/'+this.cacheKey+'/members/'+userId+'/'+fieldName;
      }

      // If cache recalculate necessary, set refreshing flag; will be unset with last cache update
      if (fieldName === 'managerEmail') {
        this.refreshing.next(true);
      }

      // Set up and make call to server
      const res: any = await this.api.t2Server('directory/edit', {
        email: this.user.email, 
        id_token: idToken,
        access_token: accessToken,
        service: this.service,
        objectType: 'user',
        objectId: userId,
        fieldName: fieldName,
        metaData: metaData,
        cachePath: cachePath,
        oldVal: oldValue,
        newVal: newValue,
      });
      
      if (!!res && res.status === 'success') {
        this.utils.logInfo('editUser call successful');    
        return 'success';
      } else {
        this.utils.logInfo('editUser call failed, status: ' + (!!res ? res.status : 'response missing'));    
        throw new Error('editUser returned bad status');
      }  
    } catch (error) {  
      this.utils.logError('editUser call error', error);    
      throw error;
    }
  }  

  // Edit custom field (action is insert or delete)
  public async editCustomField(action: string, fieldName: string): Promise<string> {
    this.utils.logInfo('editCustomField called');
    if (this.service !== 'google') { // add microsoft later
      throw new Error('editCustomField called with bad service = '+this.service); 
    }

    try {
      // Get tokens and email 
      let tokensPromise: Promise<any>;
      if (this.service === 'google') {
        tokensPromise = this.googleAuth.refreshTokens();
      } else {
        tokensPromise = Promise.resolve('skipping token refresh');
      }
      
      await tokensPromise;
      
      let idToken: string = '';
      let accessToken: string = '';
      if (this.service === 'google') {
        idToken = this.googleAuth.idToken;
        accessToken = this.googleAuth.accessToken;
      }

      const cachePath = this.utils.cacheUrl+'/teams/'+this.cacheKey+'/customFields';

      // Cache recalculate necessary, so set refreshing flag; will be unset with last cache update
      this.refreshing.next(true);

      // Set up and make call to server
      const res: any = await this.api.t2Server('directory/edit', {
        email: this.user.email, 
        id_token: idToken,
        access_token: accessToken,
        service: this.service,
        objectType: 'customField',
        objectId: 'teamtoolsCustom', // schemaId, overridden by server to this anyway
        fieldName: fieldName,
        metaData: action, // insert or delete
        cachePath: cachePath,
      });
      
      if (!!res && res.status === 'success') {
        this.utils.logInfo('editCustomField call successful');    
        return 'success';
      } else {
        this.utils.logInfo('editCustomField call failed, status: ' + (!!res ? res.status : 'response missing'));    
        throw new Error('editCustomField returned bad status');
      }  
    } catch (error) {  
      this.utils.logError('editCustomField call error', error);    
      throw error;
    }
  }  

  // Show or hide users/groups
  public async showHide(objectType: string, idArray: string[], emailArray: string[], action: string): Promise<string> {
    this.utils.logInfo('showHide called');
    if (this.service !== 'google') { // add microsoft later
      throw new Error('showHide called with bad service = '+this.service); 
    }

    try {
      // Get tokens and email 
      let tokensPromise: Promise<any>;
      if (this.service === 'google') {
        tokensPromise = this.googleAuth.refreshTokens();
      } else {
        tokensPromise = Promise.resolve('skipping token refresh');
      }
      
      await tokensPromise;
      
      let idToken: string = '';
      let accessToken: string = '';
      if (this.service === 'google') {
        idToken = this.googleAuth.idToken;
        accessToken = this.googleAuth.accessToken;
      }

      // If cache recalculate necessary, set refreshing flag; will be unset with last cache update
      // (always true for now since only users and groups can be hidden / shown)
      if (_.includes(['users','groups'], objectType)) {
        this.refreshing.next(true);
      }

      // Set up and make call to server
      const res: any = await this.api.t2Server('directory/edit', {
        email: this.user.email, 
        id_token: idToken,
        access_token: accessToken,
        service: this.service,
        cachePath: this.utils.cacheUrl+'/teams/'+this.cacheKey+'/',
        objectType: objectType, // users or groups
        objectId: idArray, // array of ids
        objectName: emailArray, // array of emails
        metaData: action, // hide or show
      });
      
      if (!!res && res.status === 'success') {
        this.utils.logInfo('showHide call successful');    
        return 'success';
      } else {
        this.utils.logInfo('showHide call failed, status: ' + (!!res ? res.status : 'response missing'));    
        throw new Error('showHide returned bad status');
      }  
    } catch (error) {  
      this.utils.logError('showHide call error', error);    
      throw error;
    }
  }  

  // Fix manager names for Japanese language ordering
  public fixManagerNames(): void {
    if (this.translate.instant('LANG') === 'ja') {
      this.members.forEach((member: TeamMember) => {
        if (member.managerName && member.managerName !== '') {
          let nameArr: string[] = _.split(member.managerName, ' ');
          let lastName: string = nameArr.pop() || '';
          nameArr = _.union([lastName], nameArr);
          member.managerName = _.join(nameArr, ' ');
        }
      });
    }
  }

  // Update Intercom with the current contact and company attributes
  // Ported from master-client/src/providers/team-data.ts updateIntercom() lines 970-1018
  // Reason: the values and the timing are owned here, but the write goes through the server. The
  // Messenger silently discards company data whenever a JWT is present, and silently drops
  // support_hidden under JWT auth, so nothing sent from the browser would land
  public updateIntercom(): void {
    if (this.utils.intercomDisable || this.utils.offline || !this.intercom.isInitialized()) {
      return;
    }

    // Reason: when impersonating, the identity on this call is the impersonator's own (their email
    // and token) but metaData belongs to the customer being impersonated, so writing it would stamp
    // the impersonated customer's attributes onto the impersonator's own contact and company
    if (this.emailImpersonate !== '') {
      this.utils.logDebug('skipping intercom update while impersonating ' + this.emailImpersonate);
      return;
    }

    const params: any = {
      supportHidden: this.isSupportHidden(),
      metaData: this.metaData,
    };
    // Reason: only send these once member data is loaded. Before that isUserAdmin / isUserOwner both
    // return false, and sending false would wrongly clear the flags for a real admin or owner
    if (this.team.members.length > 0) {
      params.isAdmin = this.isUserAdmin();
      params.isOwner = this.isUserOwner();
    }

    // Reason: this runs on every team snapshot, and each call is an http round trip plus an Intercom
    // API call, so only send when something actually changed
    const signature: string = JSON.stringify(params);
    if (signature === this.lastIntercomSignature) {
      return;
    }
    this.lastIntercomSignature = signature;

    this.getResourceFromApi('updateIntercom', params).catch((error: any) => {
      // do not surface to the user: intercom is support metadata, not app functionality
      this.lastIntercomSignature = ''; // allow a retry on the next update
      this.utils.logError('intercom update call error', error);
    });
  }
}